import { CalendarIcon, ClockIcon } from '@heroicons/react/24/solid'
import { type RefObject, useLayoutEffect, useRef, useState } from 'react'

import { QuickInterval } from '@/api/models/parking/lib/parking.types'
import { ScreenSize } from '@/app/constants'
import { useCurrentBreakpoint } from '@/app/hooks/useCurrentBreakpoint'
import { cn } from '@/app/utils'
import { useOutsideClick } from '@/hooks/useOutsideClick'

import { AppInput } from '../../input'
import { getDateLabel, getHoursDateLabel } from '../utils/getDateLabel'
import { CalendarType, DateRange } from './Calendar.types'
import { CalendarModal } from './CalendarModal'
import DatePicker from './DatePicker'
import DateRangePicker from './DateRangePicker'
import { QuickFiltersPanel } from './QuickFiltersPanel'

interface CommonProps {
  calendarType?: 'calendar' | 'hours'
  className?: string
  calendarClassName?: string
  dropdownClassName?: string
  fromMonth?: Date
  showSelectedDate?: boolean
  close?: () => void
  showQuickFilters?: boolean
  placeholder: string
  short?: boolean
  invalid?: boolean
  invalidMessage?: React.ReactNode
  dataTestId?: string
  customQuickFilters?: QuickInterval[]
  firstVisibleDate?: Date
  disabled?: {
    before?: Date
    after?: Date
  }
  today?: Date
  onOpenChange?: (isOpen: boolean) => void
  autoScrollTargetRef?: RefObject<HTMLElement | null>
  onMonthChange?: (month: Date) => void
  loading?: boolean
  individualDaysToSelect?: {
    bordered: { from: string; to: string }[]
    disabled: { from: string; to: string }[]
  }
  onFilterClick?: () => void
  /** Начало промежутка фиксировано этой датой, клик двигает только конец */
  anchorFrom?: Date
  /** «Выбрать» неактивна, пока конец промежутка не выбран */
  applyRequiresSelection?: boolean
  /** В режиме месяцев выбранной датой считается последний день месяца */
  selectMonthAsRangeEnd?: boolean
  /** В бейдже показывать только конец промежутка */
  showOnlyRangeEnd?: boolean
}

interface SingleProps extends CommonProps {
  type: CalendarType.Single
  date: Date
  onChange: (date: Date) => void
}

interface RangeProps extends CommonProps {
  type: CalendarType.Range
  dateRange: DateRange
  onRangeChange: (range?: DateRange) => void
  onRangeApply?: () => void
  formatInputValue?: (range: DateRange) => string
}

type Props = SingleProps | RangeProps

const SCROLL_TARGET_OFFSET = 20

const CalendarDropdown = (props: Props) => {
  const {
    className,
    type,
    showQuickFilters = true,
    placeholder,
    showSelectedDate,
    fromMonth,
    short,
    invalid,
    invalidMessage,
    dataTestId,
    calendarClassName,
    customQuickFilters,
    firstVisibleDate,
    disabled,
    today,
    calendarType = 'calendar',
    onOpenChange,
    autoScrollTargetRef,
    onMonthChange,
    loading = false,
    individualDaysToSelect,
    dropdownClassName,
    onFilterClick,
    anchorFrom,
    applyRequiresSelection,
    selectMonthAsRangeEnd,
    showOnlyRangeEnd
  } = props
  const { dateRange, onRangeChange, onRangeApply, formatInputValue } =
    props as RangeProps
  const { date, onChange } = props as SingleProps

  const isTypeSingle = type === CalendarType.Single
  const isTypeRange = type === CalendarType.Range

  const { breakpoint } = useCurrentBreakpoint()
  const isMobileScreen = [ScreenSize.Xxs].includes(breakpoint)

  const wrapperRef = useRef<HTMLDivElement>(null)
  const scrollPositionRef = useRef<number | null>(null)
  const wasLoadingRef = useRef(false)
  const [isOpen, setIsOpen] = useState(false)

  const onClose = () => {
    setIsOpen(false)
    onOpenChange?.(false)
  }

  useOutsideClick({
    ref: wrapperRef,
    onClose,
    isOpen,
    disabled: isMobileScreen
  })

  const saveScrollPosition = () => {
    if (!autoScrollTargetRef) {
      return
    }

    const scrollContainer = wrapperRef.current?.closest('form')
    if (scrollContainer) {
      scrollPositionRef.current = scrollContainer.scrollTop
    }
  }

  const handleRangeChange = (range?: DateRange) => {
    saveScrollPosition()
    onRangeChange(range)
  }

  const handleMonthChange = (month: Date) => {
    saveScrollPosition()
    onMonthChange?.(month)
  }

  useLayoutEffect(() => {
    if (!autoScrollTargetRef || !isOpen || isMobileScreen) {
      return
    }

    const scrollTarget = autoScrollTargetRef.current
    if (!scrollTarget) {
      return
    }

    scrollTarget.scrollIntoView(false)

    const scrollContainer = scrollTarget.closest('form')
    if (scrollContainer) {
      scrollContainer.scrollTop += SCROLL_TARGET_OFFSET
    }
  }, [autoScrollTargetRef, isOpen, isMobileScreen])

  useLayoutEffect(() => {
    if (!autoScrollTargetRef) {
      return
    }

    if (!isOpen) {
      scrollPositionRef.current = null
      wasLoadingRef.current = false
      return
    }

    if (loading) {
      wasLoadingRef.current = true
      return
    }

    if (!wasLoadingRef.current || scrollPositionRef.current === null) {
      return
    }

    const scrollContainer = wrapperRef.current?.closest('form')
    if (scrollContainer) {
      scrollContainer.scrollTop = scrollPositionRef.current
    }

    scrollPositionRef.current = null
    wasLoadingRef.current = false
  }, [autoScrollTargetRef, isOpen, loading])

  const handleToggleCalendar = () => {
    if (isOpen) {
      onClose()
      return
    }

    setIsOpen(true)
    onOpenChange?.(true)
  }

  const getInputValue = () => {
    if (isTypeRange) {
      if (calendarType === 'hours') {
        return getHoursDateLabel(dateRange || {})
      }

      return formatInputValue
        ? formatInputValue(dateRange || {})
        : getDateLabel(dateRange || {})
    } else {
      return getDateLabel({ from: date || new Date() })
    }
  }

  return (
    <div
      className="relative inline-block w-full cursor-pointer"
      ref={wrapperRef}
    >
      <div className="flex flex-col gap-8">
        <AppInput
          type="text"
          dataTestId={dataTestId}
          inputClassName="cursor-pointer"
          autoComplete="off"
          className={cn(
            'w-full border md:h-56',
            isOpen ? 'border-bronze-40' : 'border-grey-20',
            className
          )}
          readOnly
          invalid={invalid}
          invalidMessage={invalidMessage}
          onClick={handleToggleCalendar}
          value={getInputValue()}
          placeholder={placeholder}
          extraContent={
            calendarType === 'calendar' ? (
              <CalendarIcon
                onClick={handleToggleCalendar}
                className="h-24 w-24 text-grey-60"
              />
            ) : (
              <ClockIcon
                onClick={handleToggleCalendar}
                className="h-24 w-24 text-grey-60"
              />
            )
          }
        />
      </div>
      {isOpen && !isMobileScreen && (
        <div className={cn('z-10', dropdownClassName)}>
          {isTypeRange && (
            <DateRangePicker
              isLoading={loading}
              dateRange={dateRange}
              onChange={autoScrollTargetRef ? handleRangeChange : onRangeChange}
              onApply={onRangeApply}
              close={onClose}
              showSelectedDate={showSelectedDate}
              fromMonth={fromMonth}
              disabled={disabled}
              calendarType={calendarType}
              customQuickFilters={customQuickFilters}
              firstVisibleDate={firstVisibleDate}
              individualSlots={individualDaysToSelect}
              className={calendarClassName}
              onMonthChange={
                autoScrollTargetRef ? handleMonthChange : onMonthChange
              }
              anchorFrom={anchorFrom}
              applyRequiresSelection={applyRequiresSelection}
              selectMonthAsRangeEnd={selectMonthAsRangeEnd}
              showOnlyRangeEnd={showOnlyRangeEnd}
            />
          )}
          {isTypeSingle && (
            <DatePicker
              date={date}
              onChange={onChange}
              fromMonth={fromMonth}
              today={today}
              disabled={disabled}
              close={onClose}
            />
          )}
        </div>
      )}
      {showQuickFilters && !short && (
        <QuickFiltersPanel
          short={false}
          dateRange={dateRange}
          onRangeChange={
            autoScrollTargetRef ? handleRangeChange : onRangeChange
          }
          customQuickFilters={customQuickFilters || []}
          calendarType={calendarType}
          onFilterClick={onFilterClick}
        />
      )}
      {showQuickFilters && short && (
        <QuickFiltersPanel
          short={true}
          date={date}
          today={today}
          onChange={onChange}
          calendarType={calendarType}
        />
      )}
      <CalendarModal
        onClose={onClose}
        placeholder={placeholder}
        isOpen={isOpen && isMobileScreen}
      >
        {isTypeRange && (
          <DateRangePicker
            dateRange={dateRange}
            onChange={onRangeChange}
            onApply={onRangeApply}
            close={onClose}
            showSelectedDate={showSelectedDate}
            fromMonth={fromMonth}
            disabled={disabled}
            calendarType={calendarType}
            customQuickFilters={customQuickFilters}
            individualSlots={individualDaysToSelect}
            className={calendarClassName}
            isLoading={loading}
            onMonthChange={onMonthChange}
            anchorFrom={anchorFrom}
            applyRequiresSelection={applyRequiresSelection}
            selectMonthAsRangeEnd={selectMonthAsRangeEnd}
            showOnlyRangeEnd={showOnlyRangeEnd}
          />
        )}
        {isTypeSingle && (
          <DatePicker
            date={date}
            onChange={onChange}
            fromMonth={fromMonth}
            today={today}
            disabled={disabled}
            close={onClose}
          />
        )}
      </CalendarModal>
    </div>
  )
}

export { CalendarDropdown }
